import React from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { GalleryImage } from '../image-gallery/types';

interface ImageGridViewProps {
  images: GalleryImage[];
  virtualTourAvailable: boolean;
  onOpenSlider: (index: number) => void;
  onOpenVirtualTour: () => void;
  placeholderImage: string;
}

const ImageGridView: React.FC<ImageGridViewProps> = ({
  images,
  virtualTourAvailable,
  onOpenSlider,
  onOpenVirtualTour,
  placeholderImage,
}) => {
  const { t } = useTranslation();

  return (
    <Grid
      container
      spacing={2}
      className="flex w-[calc(100%-theme(space.7))] max-w-[130rem]"
      sx={{ mx: 'auto', width: '100%' }}
    >
      {/* Featured Image */}
      <Grid item xs={12} md={7} className="pl-0 pr-2">
        <div
          className="relative cursor-pointer overflow-hidden rounded-lg"
          onClick={() => onOpenSlider(virtualTourAvailable ? 1 : 0)}
          style={{ position: 'relative' }}
        >
          <div className="relative aspect-[7/5]">
            <img
              src={images[0]?.src || '/placeholder.svg'}
              alt={images[0]?.alt}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.src = placeholderImage;
              }}
            />
          </div>

          {virtualTourAvailable && (
            <div
              className="absolute bottom-4 left-4 z-10"
              style={{
                position: 'absolute',
                bottom: '16px',
                left: '16px',
                zIndex: 50,
              }}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenVirtualTour();
                }}
                className="flex items-center gap-2 rounded-[19px] bg-black/70 px-4 py-2 text-white"
              >
                <IconSaxIcon name="vr" size={24} strokeColor="none" />
                <span className="font-sans text-[16px] font-medium">
                  {t('js.components.house_overview.virtual_tour')}
                </span>
              </div>
            </div>
          )}
        </div>
      </Grid>

      {/* Thumbnail Grid */}
      <Grid item xs={12} md={5} className="pl-1">
        <Grid container spacing={2}>
          {images.slice(1, 5).map((image, index) => (
            <Grid item xs={6} sm={6} key={`thumbnail-${index + 1}`}>
              <div
                className="relative cursor-pointer overflow-hidden rounded-lg"
                onClick={() => onOpenSlider(virtualTourAvailable ? index + 2 : index + 1)}
                style={{ position: 'relative' }}
              >
                <div className="relative aspect-square">
                  <img
                    src={image?.src || '/placeholder.svg'}
                    alt={image?.alt}
                    className="h-full w-full object-cover transition-opacity hover:opacity-90"
                    onError={(e) => {
                      e.currentTarget.src = placeholderImage;
                    }}
                  />
                </div>

                {index === 3 && images.length > 5 && (
                  <div
                    className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-black/40"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenSlider(virtualTourAvailable ? 1 : 0);
                    }}
                  >
                    <div className="text-center font-bold text-white">
                      <div className="mt-1 text-[18px]">
                        +{images.length - 5} {t('js.components.house_overview.more')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ImageGridView;
